// frontend/src/components/Individual/JoinRoom.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import socket from '../../services/socket';
import Toast, { useToast } from '../Shared/Toast';
import { useScreenShare } from '../../hooks/useScreenShare';

const JoinRoom = ({ user, onLogout }) => {
  const [sessionKey, setSessionKey] = useState('');
  const [roomKey, setRoomKey] = useState('');
  const [joined, setJoined] = useState(false);
  const [finalTranscripts, setFinalTranscripts] = useState([]);
  const [liveText, setLiveText] = useState('');
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [features, setFeatures] = useState({ screenShare: false, remoteControl: false });
  const { toast } = useToast();
  const transcriptEndRef = useRef(null);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();
  const audioCtxRef = useRef(null);
  const audioEnabledRef = useRef(false);

  const ss = useScreenShare({ socket });

  // Load feature flags
  useEffect(() => {
    api.get('/settings/features').then(r => setFeatures(r.data)).catch(() => {});
  }, []);

  // Sync audioEnabled → ref
  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
    if (!audioEnabled && audioCtxRef.current) audioCtxRef.current.suspend();
    else if (audioEnabled && audioCtxRef.current) audioCtxRef.current.resume();
  }, [audioEnabled]);

  const handleAudioToggle = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
    }
    setAudioEnabled(prev => !prev);
  };

  const playPcmChunk = useCallback((audioData) => {
    if (!audioEnabledRef.current || !audioCtxRef.current) return;
    try {
      const buf = Buffer.from(audioData);
      const pcm = new Int16Array(buf.buffer, buf.byteOffset, Math.floor(buf.length / 2));
      const floats = new Float32Array(pcm.length);
      for (let i = 0; i < pcm.length; i++) floats[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
      const audioBuf = audioCtxRef.current.createBuffer(1, floats.length, 16000);
      audioBuf.copyToChannel(floats, 0);
      const source = audioCtxRef.current.createBufferSource();
      source.buffer = audioBuf;
      source.connect(audioCtxRef.current.destination);
      source.start();
    } catch (_) {}
  }, []);

  useEffect(() => { transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [finalTranscripts, liveText]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (!joined) return;

    socket.on('transcript', (data) => {
      if (data.isFinal) {
        setFinalTranscripts(prev => [...prev, { text: data.text, timestamp: data.timestamp, userId: data.userId }]);
        setLiveText('');
      } else {
        setLiveText(data.text);
      }
    });
    socket.on('message', (data) => {
      setMessages(prev => [...prev, {
        message: data.message, userId: data.userId,
        userName: data.userName || `User ${String(data.userId).slice(-4)}`,
        timestamp: data.timestamp, self: false
      }]);
    });
    socket.on('room-audio', playPcmChunk);
    socket.on('user-joined', (data) => console.log('User joined:', data));
    socket.on('error', (data) => setError(data.message));

    // WebRTC + screen share events
    socket.on('webrtc-offer-request', ss.onOfferRequest);
    socket.on('webrtc-offer', ss.onOffer);
    socket.on('webrtc-answer', ss.onAnswer);
    socket.on('webrtc-ice', ss.onIce);
    socket.on('peer-screen-share-start', ss.onPeerShareStart);
    socket.on('peer-screen-share-stop', ss.onPeerShareStop);
    socket.on('remote-pointer', ss.onRemotePointer);
    socket.on('remote-control-request', ss.onControlRequest);
    socket.on('remote-control-response', ss.onControlResponse);
    socket.on('remote-click', ss.onRemoteClick);
    socket.on('remote-scroll', ss.onRemoteScroll);
    socket.on('remote-key', ss.onRemoteKey);

    socket.connect();
    socket.once('connect', () => {
      socket.emit('join-room', { roomKey, sessionKey });
    });

    return () => {
      socket.off('transcript'); socket.off('message'); socket.off('room-audio');
      socket.off('user-joined'); socket.off('error');
      socket.off('webrtc-offer-request'); socket.off('webrtc-offer');
      socket.off('webrtc-answer'); socket.off('webrtc-ice');
      socket.off('peer-screen-share-start'); socket.off('peer-screen-share-stop');
      socket.off('remote-pointer'); socket.off('remote-control-request');
      socket.off('remote-control-response');
      socket.off('remote-click'); socket.off('remote-scroll'); socket.off('remote-key');
      socket.disconnect();
      ss.cleanup();
      if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
    };
  }, [joined]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const trimmedSession = sessionKey.trim();
      const trimmedRoom = roomKey.trim();
      await api.post('/rooms/join', { roomKey: trimmedRoom, sessionKey: trimmedSession });
      setSessionKey(trimmedSession);
      setRoomKey(trimmedRoom);
      setJoined(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim() && roomKey) {
      socket.emit('send-message', { roomKey, message: message.trim() });
      setMessages(prev => [...prev, {
        message: message.trim(), userId: user.id,
        userName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'You',
        timestamp: new Date().toISOString(), self: true
      }]);
      setMessage('');
    }
  };

  const handleLeave = () => {
    ss.stopShare();
    socket.emit('leave-room');
    socket.disconnect();
    setJoined(false);
    setFinalTranscripts([]); setLiveText(''); setMessages([]);
    setRoomKey(''); setSessionKey('');
  };

  if (joined) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Toast toast={toast} />

        {/* Remote control request banner */}
        {ss.controlRequests.map(req => (
          <div key={req.from} className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-yellow-900 border border-yellow-500 rounded-xl px-6 py-4 shadow-2xl flex items-center gap-4">
            <span className="text-yellow-200 text-sm">
              <strong>{req.fromName}</strong> wants to remotely control your screen
            </span>
            <button onClick={() => ss.respondControl(req.from, true)}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm rounded-lg">Allow</button>
            <button onClick={() => ss.respondControl(req.from, false)}
              className="px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white text-sm rounded-lg">Deny</button>
          </div>
        ))}

        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center space-x-4">
                <h1 className="text-xl font-bold text-gray-800">Room: {roomKey}</h1>
                <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">Connected</span>
                {/* Screen share button */}
                {features.screenShare && (
                  <button
                    onClick={() => ss.isSharing ? ss.stopShare() : ss.startShare(roomKey)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      ss.isSharing
                        ? 'bg-red-100 border-red-400 text-red-700 hover:bg-red-200'
                        : 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
                    }`}
                    title={ss.isSharing ? 'Stop sharing screen' : 'Share your screen with the host'}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                    </svg>
                    {ss.isSharing ? 'Stop Sharing' : 'Share Screen'}
                  </button>
                )}
                {ss.isSharing && (
                  <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
                    Broadcasting
                  </span>
                )}
                {ss.shareError && (
                  <span className="text-xs text-red-500">{ss.shareError}</span>
                )}
                {ss.controlGranted && (
                  <span className="text-xs text-yellow-600 font-medium">Remote control active</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleAudioToggle}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    audioEnabled ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-gray-100 border-gray-300 text-gray-500'
                  }`}>
                  <span className="text-base">{audioEnabled ? '🔊' : '🔇'}</span>
                  {audioEnabled ? 'Audio On' : 'No Audio'}
                </button>
                <button onClick={handleLeave}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                  Leave Room
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Remote pointer overlay — shows where interviewer is pointing on this user's screen */}
        {Object.entries(ss.remotePointers).map(([peerId, pos]) => (
          <div key={peerId} className="fixed pointer-events-none z-50"
            style={{
              left: `${pos.x * 100}%`,
              top: `${pos.y * 100}%`,
              transform: 'translate(-50%,-50%)',
            }}>
            <div className="h-6 w-6 rounded-full bg-yellow-400 opacity-80 border-2 border-yellow-600 shadow-lg" />
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs bg-gray-900 text-white px-1 rounded whitespace-nowrap">Interviewer</span>
          </div>
        ))}

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Remote shares from others in the room (if they are also sharing) */}
          {ss.remoteShares.length > 0 && (
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {ss.remoteShares.map(share => (
                <ScreenShareViewer
                  key={share.peerId}
                  share={share}
                  canControl={false}
                  onPointer={null}
                  onClick={null}
                />
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Live Transcript */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Live Transcript</h2>
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-600">Listening...</span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-y-auto font-mono text-sm leading-relaxed">
                {finalTranscripts.length === 0 && !liveText && (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-400 text-sm font-sans">Waiting for transcripts...</p>
                  </div>
                )}
                {finalTranscripts.map((t, i) => (
                  <div key={i} className="mb-3 border-b border-gray-200 pb-2 last:border-0">
                    <p className="text-gray-800 leading-relaxed">{t.text}</p>
                    <span className="text-xs text-gray-400">{new Date(t.timestamp).toLocaleTimeString()}</span>
                  </div>
                ))}
                {liveText && (
                  <div className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-blue-400 animate-pulse flex-shrink-0" />
                    <p className="text-blue-500 italic">{liveText}</p>
                  </div>
                )}
                <div ref={transcriptEndRef} />
              </div>
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <p className="text-sm text-blue-700">
                  <strong>Read-only:</strong> Viewing live transcripts from the host.
                  {features.screenShare && ' Use the Share Screen button to show your screen.'}
                </p>
              </div>
            </div>

            {/* Chat */}
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Chat</h2>
              <div className="bg-gray-50 rounded-lg p-4 flex-1 overflow-y-auto mb-4" style={{ minHeight: '320px', maxHeight: '384px' }}>
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-400 text-sm text-center">No messages yet. Say hello!</p>
                  </div>
                ) : messages.map((msg, i) => (
                  <div key={i} className={`mb-3 ${msg.self ? 'text-right' : 'text-left'}`}>
                    {!msg.self && <p className="text-xs text-gray-500 font-medium mb-0.5 ml-1">{msg.userName}</p>}
                    <div className={`inline-block px-4 py-2 rounded-lg max-w-xs ${msg.self ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-800'}`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-xs opacity-75 mt-1">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                    </div>
                    {msg.self && <p className="text-xs text-gray-400 mt-0.5 mr-1">You</p>}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={handleSendMessage} className="flex space-x-2">
                <input type="text" value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm" />
                <button type="submit" disabled={!message.trim()}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50">Send</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Join Form ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Toast toast={toast} />
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button onClick={() => navigate('/dashboard')} className="mr-4 text-gray-600 hover:text-gray-900">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-800">Join Room</h1>
            </div>
            <div className="flex items-center">
              <button onClick={onLogout} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">Logout</button>
            </div>
          </div>
        </div>
      </nav>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full mb-4">
              <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Join a Room</h2>
            <p className="text-gray-600">Enter the room key and your session key to join</p>
          </div>
          {error && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <form onSubmit={handleJoin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Room Key</label>
              <input type="text" required value={roomKey} onChange={e => setRoomKey(e.target.value.trim())}
                placeholder="Enter room key shared by host"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session Key</label>
              <input type="text" required value={sessionKey} onChange={e => setSessionKey(e.target.value.trim())}
                placeholder="Enter your session key from company"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm" />
              <p className="mt-2 text-sm text-gray-500">Get your session key from your company administrator</p>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-medium rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 shadow-lg">
              {loading ? 'Joining...' : 'Join Room'}
            </button>
          </form>

          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">How it works:</h3>
            <ol className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-purple-100 text-purple-600 rounded-full mr-3 text-xs font-bold">1</span>
                <span>Get the room key from the person who created the room</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-purple-100 text-purple-600 rounded-full mr-3 text-xs font-bold">2</span>
                <span>Enter your session key provided by your company</span>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-purple-100 text-purple-600 rounded-full mr-3 text-xs font-bold">3</span>
                <span>View live transcripts and chat with the host in real-time</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Screen Share Viewer sub-component ─────────────────────────────────────────
const ScreenShareViewer = ({ share, canControl, onPointer, onClick, controlGranted, onRequestControl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && share.stream) {
      videoRef.current.srcObject = share.stream;
    }
  }, [share.stream]);

  const handleMouseMove = (e) => {
    if (!canControl || !onPointer) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top)  / rect.height;
    onPointer(share.peerId, x, y);
  };

  const handleClick = (e) => {
    if (!canControl || !controlGranted || !onClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top)  / rect.height;
    onClick(share.peerId, x, y);
  };

  return (
    <div className="bg-gray-900 rounded-xl overflow-hidden shadow-lg border border-gray-700">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-gray-200 text-xs font-medium">{share.peerName || 'Participant'} — Screen</span>
        </div>
        {canControl && !controlGranted && onRequestControl && (
          <button onClick={() => onRequestControl(share.peerId)}
            className="text-xs px-2 py-1 bg-yellow-700 hover:bg-yellow-600 text-yellow-100 rounded transition-colors">
            Request Control
          </button>
        )}
        {controlGranted && (
          <span className="text-xs text-yellow-400">Control active</span>
        )}
      </div>
      <div className="relative">
        {share.stream ? (
          <video ref={videoRef} autoPlay playsInline muted
            className={`w-full ${canControl ? (controlGranted ? 'cursor-crosshair' : 'cursor-pointer') : 'cursor-default'}`}
            onMouseMove={handleMouseMove}
            onClick={handleClick}
          />
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-gray-800">
            <div className="text-center">
              <div className="h-8 w-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-gray-400 text-xs">Connecting...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinRoom;
